import { useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import Nestable from "react-nestable";
import { toast } from "react-toastify";
import swal from "sweetalert";
import { useTranslation } from "react-i18next";
import "../../../../i18n";
import {
  addCategoriesToMenu,
  createCategoryQuick,
  createProductQuick,
  deleteCategoryFromMenu,
  deleteProductFromCategory,
  fetchAllMenus,
  fetchCategories,
  fetchMenuById,
  fetchProducts,
  updateCategoryOrder,
  updateMenuCategoryHideFromSummary,
  updateMenuCategoryHideTitleFromClient,
  updateMenuCategorySummaryTarget,
  updateMenuSubcategoryProductsVisibility,
} from "../../../../services/MenuService";
import {
  createSubCategoryQuick,
  fetchActiveSubCategories,
} from "../../../../services/subcategories";
import { confirmReorder } from "../../../../utils/confirmDialogs";
import AssignPickerModal from "./AssignPickerModal";
import CategorySettingsModal from "./CategorySettingsModal";
import MenuTreeCategoryNode from "./MenuTreeCategoryNode";
import { gripHandle } from "./menuTreeHandles";
import { fetchRestoDataByUser } from "../../../../services/restaurantService";
import WizardLivePhone from "../../Setup/StartNowWizard/WizardLivePhone";
import {
  buildManageMenuLivePreview,
  categoryTitle,
  menuTitle,
  normalizeRef,
  productTitle,
  subcategoryTitle,
} from "./menuTreeUtils";
import useDragAutoScroll from "./useDragAutoScroll";
import useStableNestable from "./useStableNestable";
import "./MenuTree.css";

const productsOfSub = (sub) =>
  (sub?.productList || [])
    .filter((p) => p?.product != null && p.product.isArchived !== true)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((p) => {
      const id = normalizeRef(p.product);
      if (!id) return null;
      return {
        id,
        label: productTitle(typeof p.product === "object" ? p.product : null),
      };
    })
    .filter(Boolean);

const childProductKey = (categoryId, subId) => `${categoryId}:${subId}`;

const sameIdList = (a = [], b = []) =>
  a.length === b.length && a.every((id) => b.includes(id));

/**
 * Manage-menu tree:
 *   Menu
 *   ├─ Category ─ products
 *   └─ Category ─ Subcategory ─ products
 */
const MenuTree = ({ menuId, onEditMenu, onBackToMenus }) => {
  const { t } = useTranslation();
  const autoScroll = useDragAutoScroll();
  const [menu, setMenu] = useState(null);
  const [categories, setCategories] = useState([]); // all restaurant categories + per-menu flags
  const [menuCategoryIds, setMenuCategoryIds] = useState([]); // ordered ids present in this menu
  const [allMenus, setAllMenus] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allSubcategories, setAllSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restaurant, setRestaurant] = useState(null);
  const [previewByCategory, setPreviewByCategory] = useState({});

  const [expanded, setExpanded] = useState({});
  const [settingsCategoryId, setSettingsCategoryId] = useState(null);
  const [settingsBusy, setSettingsBusy] = useState(false);
  // Snapshot of the per-menu flags when the settings modal opened, so "Cancel" can revert.
  const [settingsSnapshot, setSettingsSnapshot] = useState(null);

  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [pickerSubmitting, setPickerSubmitting] = useState(false);
  /** { [categoryId]: subcategoryId[] } — missing = keep every subcategory. */
  const [categoryKeptChildren, setCategoryKeptChildren] = useState({});
  const [initialCategoryKeptChildren, setInitialCategoryKeptChildren] = useState({});
  /** { [`${categoryId}:${subId}`]: productId[] } — missing = keep every product. */
  const [categoryKeptProducts, setCategoryKeptProducts] = useState({});
  const [initialCategoryKeptProducts, setInitialCategoryKeptProducts] = useState({});

  // ---------------------------------------------------------------- loading
  const reload = useCallback(async () => {
    const [categoriesData, menuData, menusData] = await Promise.all([
      fetchCategories(),
      fetchMenuById(menuId),
      fetchAllMenus(),
    ]);
    setMenu(menuData || null);
    setAllMenus(Array.isArray(menusData) ? menusData : []);

    const list = Array.isArray(menuData?.categoriesList) ? menuData.categoriesList : [];
    const byCategory = Object.fromEntries(
      list.map((entry) => [normalizeRef(entry.category), entry]).filter(([id]) => id)
    );

    const decorated = (Array.isArray(categoriesData) ? categoriesData : []).map((category) => {
      const entry = byCategory[String(category._id)];
      return {
        ...category,
        order: entry?.order ?? 0,
        menuHideCategoryTitle: entry?.hideTitleFromClient === true,
        menuHideFromSummary: entry?.hideFromSummary === true,
        menuSummaryTargetCategoryId:
          entry?.summaryTargetCategoryId != null ? String(entry.summaryTargetCategoryId) : "",
        menuSummaryTargetSubCategoryId:
          entry?.summaryTargetSubCategoryId != null ? String(entry.summaryTargetSubCategoryId) : "",
      };
    });
    setCategories(decorated);

    const ordered = decorated
      .filter((c) => byCategory[String(c._id)])
      .sort((a, b) => a.order - b.order)
      .map((c) => String(c._id));
    setMenuCategoryIds(ordered);
    setExpanded((prev) => {
      const next = { ...prev };
      ordered.forEach((id) => {
        if (next[id] === undefined) next[id] = true;
      });
      return next;
    });
  }, [menuId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await reload();
        const [products, subs, restoData] = await Promise.all([
          fetchProducts(),
          fetchActiveSubCategories(),
          fetchRestoDataByUser().catch(() => null),
        ]);
        if (cancelled) return;
        setAllProducts(Array.isArray(products) ? products : []);
        setAllSubcategories(Array.isArray(subs) ? subs : []);
        setRestaurant(restoData?.restaurant ?? restoData ?? null);
      } catch (err) {
        console.error("MenuTree load error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  // ---------------------------------------------------------------- derived
  const categoriesById = useMemo(
    () => Object.fromEntries(categories.map((c) => [String(c._id), c])),
    [categories]
  );
  const menuCategories = useMemo(
    () => menuCategoryIds.map((id) => categoriesById[id]).filter(Boolean),
    [menuCategoryIds, categoriesById]
  );
  const title = menu ? menuTitle(menu, "") : "";

  const handlePreviewStructure = useCallback((categoryId, structure) => {
    const next = {
      productIds: structure?.productIds || [],
      subcategories: structure?.subcategories || [],
    };
    setPreviewByCategory((prev) => {
      const current = prev[categoryId];
      if (
        current &&
        current.productIds.join("|") === next.productIds.join("|") &&
        current.subcategories.length === next.subcategories.length &&
        current.subcategories.every(
          (sub, index) =>
            sub.id === next.subcategories[index].id &&
            sub.productIds.join("|") === next.subcategories[index].productIds.join("|") &&
            (sub.hideTitleFromClient === true) ===
              (next.subcategories[index].hideTitleFromClient === true)
        )
      ) {
        return prev;
      }
      return { ...prev, [categoryId]: next };
    });
  }, []);

  // Stable Nestable props: a new reference mid-drag would cancel the drag.
  const categoryRows = useMemo(() => menuCategoryIds.map((id) => ({ id })), [menuCategoryIds]);
  const categoryGrip = useMemo(() => gripHandle(t("menuTreeDragHint")), [t]);

  const summaryTargetCategoryOptions = useMemo(() => {
    const categoryToMenus = {};
    allMenus.forEach((m) => {
      const label = menuTitle(m);
      (Array.isArray(m.categoriesList) ? m.categoriesList : []).forEach((entry) => {
        const cid = normalizeRef(entry?.category);
        if (!cid) return;
        if (!categoryToMenus[cid]) categoryToMenus[cid] = new Set();
        categoryToMenus[cid].add(label);
      });
    });
    return categories.map((c) => {
      const id = String(c._id);
      const menus = categoryToMenus[id] ? Array.from(categoryToMenus[id]) : [];
      const label = categoryTitle(c);
      return { value: id, label: menus.length ? `${label} (${menus.join(", ")})` : label };
    });
  }, [categories, allMenus]);

  const summaryTargetSubcategoryOptionsByCategory = useMemo(() => {
    const map = {};
    categories.forEach((c) => {
      map[String(c._id)] = (Array.isArray(c.subcategories) ? c.subcategories : [])
        .map((s) => {
          const id = s?.subCategory?._id != null ? String(s.subCategory._id) : null;
          return id ? { value: id, label: subcategoryTitle(s.subCategory, "Subcategory") } : null;
        })
        .filter(Boolean);
    });
    return map;
  }, [categories]);

  // ---------------------------------------------------------------- category order
  const handleReorderCategories = async (orderedIds) => {
    const previous = menuCategoryIds;
    if (!(await confirmReorder(t, "category"))) {
      // Nestable already shows the dropped order; a fresh array reference makes it resync.
      setMenuCategoryIds([...previous]);
      return;
    }
    setMenuCategoryIds(orderedIds);
    try {
      for (let index = 0; index < orderedIds.length; index += 1) {
        await updateCategoryOrder(menuId, orderedIds[index], index);
      }
      toast.success(t("menuCategoryOrderSaved"));
    } catch (err) {
      setMenuCategoryIds(previous);
      toast.error(err?.response?.data?.message || t("menuOrderSaveFailed"));
      await reload();
    }
  };

  // ---------------------------------------------------------------- category settings
  const runSetting = async (fn) => {
    setSettingsBusy(true);
    try {
      await fn();
      await reload();
      toast.success(t("menuUpdatedSuccess"));
    } catch (err) {
      toast.error(err?.response?.data?.message || t("errorUpdating"));
    } finally {
      setSettingsBusy(false);
    }
  };

  const handleTitleVisible = (categoryId, visible) =>
    runSetting(() => updateMenuCategoryHideTitleFromClient(menuId, categoryId, !visible));
  const handleSummaryVisible = (categoryId, visible) =>
    runSetting(() => updateMenuCategoryHideFromSummary(menuId, categoryId, !visible));
  const handleSummaryTarget = (categoryId, targetCategoryId, targetSubCategoryId) =>
    runSetting(() =>
      updateMenuCategorySummaryTarget(menuId, categoryId, targetCategoryId, targetSubCategoryId)
    );

  const snapshotSettings = (category) => ({
    hideTitle: category.menuHideCategoryTitle === true,
    hideFromSummary: category.menuHideFromSummary === true,
    targetCategoryId: category.menuSummaryTargetCategoryId || "",
    targetSubCategoryId: category.menuSummaryTargetSubCategoryId || "",
  });

  const openSettings = (category) => {
    setSettingsSnapshot(snapshotSettings(category));
    setSettingsCategoryId(String(category._id));
  };

  const closeSettings = () => {
    setSettingsCategoryId(null);
    setSettingsSnapshot(null);
  };

  /** "Cancel": put back whatever was in place when the modal opened. */
  const cancelSettings = async () => {
    const categoryId = settingsCategoryId;
    const before = settingsSnapshot;
    const current = categoryId ? categoriesById[categoryId] : null;
    closeSettings();
    if (!before || !current) return;

    const now = snapshotSettings(current);
    const calls = [];
    if (now.hideTitle !== before.hideTitle) {
      calls.push(() => updateMenuCategoryHideTitleFromClient(menuId, categoryId, before.hideTitle));
    }
    if (now.hideFromSummary !== before.hideFromSummary) {
      calls.push(() => updateMenuCategoryHideFromSummary(menuId, categoryId, before.hideFromSummary));
    }
    if (
      now.targetCategoryId !== before.targetCategoryId ||
      now.targetSubCategoryId !== before.targetSubCategoryId
    ) {
      calls.push(() =>
        updateMenuCategorySummaryTarget(
          menuId,
          categoryId,
          before.targetCategoryId || null,
          before.targetSubCategoryId || null
        )
      );
    }
    if (!calls.length) return;

    setSettingsBusy(true);
    try {
      for (const call of calls) await call();
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.message || t("errorUpdating"));
      await reload();
    } finally {
      setSettingsBusy(false);
    }
  };

  const subsById = useMemo(
    () => Object.fromEntries((allSubcategories || []).map((s) => [String(s._id), s])),
    [allSubcategories]
  );

  /** Every category of the restaurant, with its subcategories and products. */
  const productsById = useMemo(
    () => Object.fromEntries((allProducts || []).map((p) => [String(p._id), p])),
    [allProducts]
  );

  const livePreview = useMemo(
    () =>
      buildManageMenuLivePreview({
        menu,
        restaurant,
        menuCategories,
        previewByCategory,
        productsById,
        subsById,
      }),
    [menu, restaurant, menuCategories, previewByCategory, productsById, subsById]
  );

  const availableCategories = useMemo(
    () =>
      categories.map((c) => ({
        id: String(c._id),
        label: categoryTitle(c),
        products: (Array.isArray(c.productList) ? c.productList : [])
          .filter((p) => p?.product != null)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((p) => {
            const id = normalizeRef(p.product);
            if (!id) return null;
            const product =
              typeof p.product === "object" && p.product.productDetails
                ? p.product
                : productsById[id];
            if (product?.isArchived === true) return null;
            return { id, label: productTitle(product) };
          })
          .filter(Boolean),
        children: (Array.isArray(c.subcategories) ? c.subcategories : [])
          .slice()
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((entry) => {
            const id = normalizeRef(entry.subCategory);
            const sub = id ? subsById[id] : null;
            if (!sub) return null;
            return { id, label: subcategoryTitle(sub), products: productsOfSub(sub) };
          })
          .filter(Boolean),
      })),
    [categories, subsById, productsById]
  );

  const visibilityFromMenu = (items) => {
    const hiddenByKey = {};
    const hiddenSubs = new Set();
    (menu?.categoriesList || []).forEach((entry) => {
      const catId = normalizeRef(entry.category);
      if (!catId) return;
      (entry.hiddenSubcategoryProducts || []).forEach((h) => {
        const subId = normalizeRef(h.subCategory);
        if (!subId) return;
        hiddenByKey[childProductKey(catId, subId)] = (h.products || []).map((id) => String(id));
      });
      (entry.hiddenSubcategories || []).forEach((id) => {
        const subId = normalizeRef(id);
        if (subId) hiddenSubs.add(`${catId}:${subId}`);
      });
    });
    const childrenKept = {};
    const productsKept = {};
    items.forEach((item) => {
      const children = item.children || [];
      if (!children.length) return;
      const keptSubs = [];
      children.forEach((child) => {
        const all = (child.products || []).map((p) => p.id);
        const hidden = hiddenByKey[childProductKey(item.id, child.id)] || [];
        const visible = all.filter((id) => !hidden.includes(id));
        const dropped =
          hiddenSubs.has(childProductKey(item.id, child.id)) ||
          (all.length > 0 && visible.length === 0);
        if (dropped) {
          productsKept[childProductKey(item.id, child.id)] = [];
          return;
        }
        keptSubs.push(child.id);
        if (hidden.length) productsKept[childProductKey(item.id, child.id)] = visible;
      });
      childrenKept[item.id] = keptSubs;
    });
    return { childrenKept, productsKept };
  };

  // ---------------------------------------------------------------- add / remove categories
  const openCategoryPicker = () => {
    const vis = visibilityFromMenu(availableCategories);
    setSelectedCategoryIds([...menuCategoryIds]);
    setCategoryKeptChildren(vis.childrenKept);
    setInitialCategoryKeptChildren(vis.childrenKept);
    setCategoryKeptProducts(vis.productsKept);
    setInitialCategoryKeptProducts(vis.productsKept);
    setCategoryPickerOpen(true);
  };

  /**
   * "+ New category" inside the picker: create it, append it to the list and
   * hand back its id so the modal ticks it. Resolves undefined on failure.
   */
  const handleCreateCategory = async (name) => {
    try {
      const created = await createCategoryQuick(name);
      if (!created?._id) return undefined;
      const newId = String(created._id);
      setCategories((prev) =>
        prev.some((c) => String(c._id) === newId)
          ? prev
          : [
              ...prev,
              {
                ...created,
                order: 0,
                menuHideCategoryTitle: false,
                menuHideFromSummary: false,
                menuSummaryTargetCategoryId: "",
                menuSummaryTargetSubCategoryId: "",
              },
            ]
      );
      toast.success(t("categoryCreated"));
      return newId;
    } catch (err) {
      console.error("MenuTree create category error:", err);
      toast.error(err?.response?.data?.message || t("errorUpdating"));
      return undefined;
    }
  };

  /** "+ New product" inside a category / subcategory picker. */
  const handleCreateProduct = async (name) => {
    try {
      const created = await createProductQuick(name);
      if (!created?._id) return undefined;
      const newId = String(created._id);
      setAllProducts((prev) =>
        prev.some((p) => String(p._id) === newId) ? prev : [...prev, created]
      );
      toast.success(t("productCreated"));
      return newId;
    } catch (err) {
      console.error("MenuTree create product error:", err);
      toast.error(err?.response?.data?.message || t("errorUpdating"));
      return undefined;
    }
  };

  /** "+ New subcategory" inside a category picker. */
  const handleCreateSubcategory = async (name) => {
    try {
      const created = await createSubCategoryQuick(name);
      if (!created?._id) return undefined;
      const newId = String(created._id);
      setAllSubcategories((prev) =>
        prev.some((s) => String(s._id) === newId)
          ? prev
          : [...prev, { ...created, productList: created.productList || [] }]
      );
      toast.success(t("subcategoryCreated"));
      return newId;
    } catch (err) {
      console.error("MenuTree create subcategory error:", err);
      toast.error(err?.response?.data?.message || t("errorUpdating"));
      return undefined;
    }
  };

  const applyCategoryDirectProducts = async (categoryIds) => {
    const itemsById = Object.fromEntries(availableCategories.map((item) => [item.id, item]));
    for (const catId of categoryIds) {
      const all = (itemsById[catId]?.products || []).map((p) => p.id);
      if (!all.length) continue;
      const custom = categoryKeptProducts[catId];
      if (!Array.isArray(custom)) continue;
      const kept = custom.filter((id) => all.includes(id));
      const hidden = all.filter((id) => !kept.includes(id));
      if (hidden.length) {
        await deleteProductFromCategory(menuId, catId, hidden);
      }
    }
  };

  const applyCategoryContentsVisibility = async (categoryIds) => {
    const itemsById = Object.fromEntries(availableCategories.map((item) => [item.id, item]));
    for (const catId of categoryIds) {
      const children = itemsById[catId]?.children || [];
      if (!children.length) continue;
      const keptSubs = Array.isArray(categoryKeptChildren[catId])
        ? categoryKeptChildren[catId]
        : children.map((c) => c.id);
      for (const child of children) {
        const all = (child.products || []).map((p) => p.id);
        const keepSub = keptSubs.includes(child.id);
        const custom = categoryKeptProducts[childProductKey(catId, child.id)];
        const keptIds = keepSub ? (Array.isArray(custom) ? custom : all) : [];
        const hidden = keepSub ? all.filter((id) => !keptIds.includes(id)) : all;
        await updateMenuSubcategoryProductsVisibility(menuId, catId, child.id, hidden, {
          hideSubcategory: !keepSub,
        });
      }
    }
  };

  const handleCategoryPickerSubmit = async () => {
    const toRemove = menuCategoryIds.filter((id) => !selectedCategoryIds.includes(id));
    const toAdd = selectedCategoryIds.filter((id) => !menuCategoryIds.includes(id));
    const stayingChanged = selectedCategoryIds.filter((id) => {
      if (!menuCategoryIds.includes(id)) return false;
      const children = availableCategories.find((item) => item.id === id)?.children || [];
      if (!children.length) return false;
      const nowSubs = categoryKeptChildren[id] ?? children.map((c) => c.id);
      const initSubs = initialCategoryKeptChildren[id] ?? children.map((c) => c.id);
      if (!sameIdList(nowSubs, initSubs)) return true;
      return children.some((child) => {
        const all = (child.products || []).map((p) => p.id);
        const key = childProductKey(id, child.id);
        return !sameIdList(categoryKeptProducts[key] ?? all, initialCategoryKeptProducts[key] ?? all);
      });
    });
    if (toRemove.length === 0 && toAdd.length === 0 && stayingChanged.length === 0) return;
    setPickerSubmitting(true);
    try {
      if (toRemove.length > 0) {
        await deleteCategoryFromMenu(menuId, toRemove);
      }
      if (toAdd.length > 0) {
        const base = menuCategoryIds.length - toRemove.length;
        await addCategoriesToMenu(
          menuId,
          toAdd.map((categoryId, index) => ({ categoryId, order: base + index + 1 }))
        );
        await applyCategoryDirectProducts(toAdd);
      }
      await applyCategoryContentsVisibility([...stayingChanged, ...toAdd]);
      await reload();
      toast.success(t("menuUpdatedSuccess"));
      setCategoryPickerOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || t("errorUpdating"));
    } finally {
      setPickerSubmitting(false);
    }
  };

  const handleRemoveCategory = async (category) => {
    const ok = await swal({
      title: t("areYouSure"),
      text: t("menuTreeRemoveConfirm", { name: categoryTitle(category), parent: title }),
      icon: "warning",
      buttons: [t("cancel"), t("remove")],
      dangerMode: true,
    });
    if (!ok) return;
    try {
      await deleteCategoryFromMenu(menuId, [String(category._id)]);
      await reload();
      toast.success(t("menuTreeRemoved"));
    } catch (err) {
      toast.error(err?.response?.data?.message || t("errorUpdating"));
    }
  };

  // ---------------------------------------------------------------- render
  // Stable Nestable callbacks: a new reference mid-drag would cancel the drag.
  const nestable = useStableNestable({
    renderItem: ({ item, handler }) => {
      const category = categoriesById[item.id];
      if (!category) return null;
      return (
        <MenuTreeCategoryNode
          menuId={menuId}
          category={category}
          dragHandle={handler}
          expanded={expanded[item.id] !== false}
          onToggleExpand={() =>
            setExpanded((prev) => ({ ...prev, [item.id]: prev[item.id] === false }))
          }
          onOpenSettings={openSettings}
          onRemove={handleRemoveCategory}
          allProducts={allProducts}
          allSubcategories={allSubcategories}
          onCreateProduct={handleCreateProduct}
          onCreateSubcategory={handleCreateSubcategory}
          onPreviewStructure={handlePreviewStructure}
          t={t}
        />
      );
    },
    onChange: ({ items }) => {
      if (!Array.isArray(items)) return;
      const orderedIds = items.map((row) => row.id);
      const unchanged = orderedIds.every((id, i) => menuCategoryIds[i] === id);
      if (!unchanged) handleReorderCategories(orderedIds);
    },
  });

  return (
    <div className="menu-manage-layout">
      <div className="menu-tree">
      <div className="menu-tree__header">
        <span className="menu-tree__icon" aria-hidden>
          <i className="fas fa-utensils" />
        </span>
        <h2 className={`menu-tree__title ${!title ? "menu-tree__title--placeholder" : ""}`} title={title}>
          {title || t("menu")}
        </h2>
        <Dropdown align="end" popperConfig={{ strategy: "fixed" }}>
          <Dropdown.Toggle
            variant="light"
            className="menu-tree__kebab"
            id="menu-tree-actions"
            title={t("actions")}
          >
            <i className="fas fa-ellipsis-v" />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={openCategoryPicker}>
              <i className="fas fa-folder-plus me-2 text-secondary" />
              {t("menuTreeAddCategory")}
            </Dropdown.Item>
            {onEditMenu && (
              <Dropdown.Item onClick={onEditMenu}>
                <i className="fas fa-pen me-2 text-warning" />
                {t("editMenu")}
              </Dropdown.Item>
            )}
            {onBackToMenus && (
              <>
                <Dropdown.Divider />
                <Dropdown.Item onClick={onBackToMenus}>
                  <i className="fas fa-arrow-left me-2 text-secondary" />
                  {t("backToMenus")}
                </Dropdown.Item>
              </>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <p className="menu-tree__subtitle">{t("menuTreeSubtitle")}</p>
      {menuCategories.length > 0 && (
        <p className="menu-tree__order-hint">
          <i className="fas fa-grip-vertical" aria-hidden />
          {t("menuTreeOrderHint")}
        </p>
      )}
      <hr className="menu-tree__divider" />

      {loading ? (
        <div className="menu-tree__loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t("loading")}</span>
          </div>
        </div>
      ) : menuCategories.length === 0 ? (
        <p className="menu-tree__no-categories">{t("menuTreeNoCategories")}</p>
      ) : (
        <div ref={autoScroll.rootRef}>
        <Nestable
          group={nestable.group}
          items={categoryRows}
          maxDepth={1}
          handler={categoryGrip}
          onDragStart={autoScroll.onDragStart}
          onDragEnd={autoScroll.onDragEnd}
          renderItem={nestable.renderItem}
          onChange={nestable.onChange}
        />
        </div>
      )}

      <button type="button" className="btn menu-tree__add-category" onClick={openCategoryPicker}>
        + {t("menuTreeAddCategory")}
      </button>

      <AssignPickerModal
        show={categoryPickerOpen}
        onHide={() => setCategoryPickerOpen(false)}
        title={t("menuTreeAddTo", { name: title || t("menu") })}
        className="menu-picker--categories"
        items={availableCategories}
        selectedIds={selectedCategoryIds}
        initialSelectedIds={menuCategoryIds}
        onToggle={(id) =>
          setSelectedCategoryIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
          )
        }
        onSetSelected={setSelectedCategoryIds}
        kept={categoryKeptProducts}
        initialKept={initialCategoryKeptProducts}
        onKeptChange={(key, ids) => setCategoryKeptProducts((prev) => ({ ...prev, [key]: ids }))}
        keptChildren={categoryKeptChildren}
        initialKeptChildren={initialCategoryKeptChildren}
        onKeptChildrenChange={(catId, ids) =>
          setCategoryKeptChildren((prev) => ({ ...prev, [catId]: ids }))
        }
        onSubmit={handleCategoryPickerSubmit}
        submitting={pickerSubmitting}
        searchPlaceholder={t("enterCatName")}
        onCreate={handleCreateCategory}
        createLabel={t("menuTreeNewCategory")}
        createPlaceholder={t("menuTreeNewCategoryPlaceholder")}
        t={t}
      />

      <CategorySettingsModal
        show={Boolean(settingsCategoryId)}
        onHide={closeSettings}
        onCancel={cancelSettings}
        category={settingsCategoryId ? categoriesById[settingsCategoryId] : null}
        busy={settingsBusy}
        onTitleVisibleChange={handleTitleVisible}
        onSummaryVisibleChange={handleSummaryVisible}
        onSummaryTargetChange={handleSummaryTarget}
        summaryTargetCategoryOptions={summaryTargetCategoryOptions}
        summaryTargetSubcategoryOptionsByCategory={summaryTargetSubcategoryOptionsByCategory}
        t={t}
      />
    </div>

      <aside className="menu-manage-live" aria-label={t("wizardPreviewLive")}>
        <WizardLivePhone
          draft={livePreview.draft}
          menuPhotoPreview={livePreview.menuPhotoPreview}
          productPhotos={livePreview.productPhotos}
          compact
          showStepLabel={false}
          emptyHintKey="menuTreeLiveEmpty"
        />
      </aside>
    </div>
  );
};

export default MenuTree;
